import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Modal,
  TextField,
  DatePicker,
  FileUploader,
  Button,
  TextArea,
  Loader,
  LoaderSizes,
  Iconography,
  IconographySizes,
  IconographyThemes,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../../../redux";
import {
  updateEnvironmentDetailsSagaAction,
  getProjectDetailsSagaAction,
} from "../../../../../../../../redux/saga/projects/saga-actions";
import { getFileViewUrlSagaAction } from "../../../../../../../../redux/saga/onboarding-form/saga-actions";
import { projectsSliceAction } from "../../../../../../../../redux/slices/projects";
import { projectValidationSliceAction } from "../../../../../../../../redux/slices/project-validation";
import {
  UpdateEnvironmentDetailsRequest,
  IntegrationDetail,
} from "../../../../../../../../redux/slices/projects/Types";
import { EnvironmentDetails } from "../../../../../../../../redux/slices/project-validation/Types";
import { getEnvironmentDetailsSagaAction } from "../../../../../../../../redux/saga/project-validation/saga-actions";
import {
  getFormFields,
  getFormTitle,
  FormFieldConfig,
} from "./environmentFormConfig";
import {
  usePartnerSessionCanEditModule,
  usePartnerSessionModuleReadOnly,
} from "../../../../../../../../hooks/usePartnerSessionPermissions";
import { PARTNER_PORTAL_PERMISSION_KEYS as PP_KEYS } from "../../../../../../../../utils/permissions/partnerPortalPermissionKeys";
import "./Modals.scss";

// ========== TEMPORARY: Data entry partner view emails - start (remove with TEMPORARY block) ==========
const DATA_ENTRY_PARTNER_VIEW_EMAILS = [
  "nithin.george@hidglobal.com",
  "swetha.patnam@hidglobal.com",
  "chris.mclean@hidglobal.com",
  "andrew.vu@hidglobal.com"
];

const isDataEntryUserEmail = (email?: string | null): boolean =>
  !!email && DATA_ENTRY_PARTNER_VIEW_EMAILS.includes(email.toLowerCase());
// ========== TEMPORARY: Data entry partner view - end ==========

export interface EnvironmentDetailsModalProps {
  isHIDUser: boolean;
  onClose: () => void;
  technology: string;
  integration: string;
  integrationId: number;
  documentationId?: number;
  existingData?: IntegrationDetail | null;
  /** When true, disables Save and Submit for partner (e.g. when Development step is not completed) */
  disableSaveSubmit?: boolean;
  /** Validation status of the documentation (e.g. "Submitted" when validation form is submitted) */
  validationStatus?: string | null;
  /** When true, prevents refetching project details on close (useful for history view to preserve form data) */
  skipProjectDetailsRefetch?: boolean;
  /** TEMPORARY: When true (history page), data-entry user (kiran) sees Verify button instead of Save/Submit. */
  isFromHistoryPage?: boolean;
}

const EnvironmentDetailsModal: React.FC<EnvironmentDetailsModalProps> = ({
  isHIDUser,
  onClose,
  technology,
  integration,
  integrationId,
  documentationId,
  existingData,
  disableSaveSubmit = false,
  validationStatus = null,
  skipProjectDetailsRefetch = false,
  isFromHistoryPage = false,
}) => {
  const dispatch = useAppDispatch();
  const { status, projectDetails } = useAppSelector((state) => state.projects);
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const { environmentDetails, environmentStatus, historyEnvironmentDetails } =
    useAppSelector((state) => state.project_validation);
  // ========== TEMPORARY: Data entry partner view - show Save/Submit for data-entry user on main tab; on history page show Verify. Remove with TEMPORARY block. ==========
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const isDataEntryUser = isDataEntryUserEmail(loggedUserDetail?.email);
  const showPartnerButtons =
    !isHIDUser || (isDataEntryUser && !isFromHistoryPage);
  // ========== TEMPORARY: end ==========
  const canEditSubmission = usePartnerSessionCanEditModule(
    PP_KEYS.VALIDATION_SUBMISSION
  );
  const submissionReadOnly = usePartnerSessionModuleReadOnly(
    PP_KEYS.VALIDATION_SUBMISSION
  );
  const canEditApproval = usePartnerSessionCanEditModule(
    PP_KEYS.VALIDATION_APPROVAL
  );
  const approvalReadOnly = usePartnerSessionModuleReadOnly(
    PP_KEYS.VALIDATION_APPROVAL
  );
  const partnerActionsLocked =
    submissionReadOnly || !canEditSubmission;
  const approvalActionsLocked = approvalReadOnly || !canEditApproval;
  const formLocked = showPartnerButtons
    ? partnerActionsLocked
    : approvalActionsLocked;
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [hasFetchedEnvironment, setHasFetchedEnvironment] = useState(false);

  // Get form fields based on technology and integration
  const formFields = useMemo(() => {
    return getFormFields(technology, integration);
  }, [technology, integration]);

  // Fetch environment details for this integration/documentation (only when a documentationId exists)
  useEffect(() => {
    if (integrationId && documentationId != null) {
      dispatch(
        getEnvironmentDetailsSagaAction({
          integration_id: integrationId,
          documentation_id: documentationId,
        }),
      );
      // Once we trigger a fetch, rely on environmentDetails slice (not existingData)
      setHasFetchedEnvironment(true);
    }
  }, [dispatch, integrationId, documentationId]);

  // Initialize form data from validation slice, history, or integration snapshot on overview
  const initializeFormData = useCallback(() => {
    const data: Record<string, any> = {};
    let source: Partial<EnvironmentDetails> | null = null;

    if (documentationId != null) {
      // For specific documentation versions, use fetched environmentDetails (may be null before load)
      source =
        (environmentDetails as Partial<EnvironmentDetails> | null) ||
        (historyEnvironmentDetails as Partial<EnvironmentDetails> | null) ||
        null;
    } else if (historyEnvironmentDetails) {
      // New version: use last history environment snapshot when available
      source = historyEnvironmentDetails as Partial<EnvironmentDetails>;
    } else if (existingData) {
      // Overview page: prefill from integration data on project details
      source = existingData as Partial<EnvironmentDetails>;
    } else {
      source = null;
    }

    formFields.forEach((field) => {
      if (field.type === "file") {
        // For file fields, store the S3 key (string) if it exists
        const existingValue = source?.[field.name as keyof typeof source];
        data[field.name] =
          (typeof existingValue === "string" ? existingValue : "") || "";
      } else if (field.type === "date") {
        const existingValue = source?.[field.name as keyof typeof source];
        data[field.name] = existingValue
          ? new Date(existingValue as string)
          : null;
      } else {
        data[field.name] =
          (source?.[field.name as keyof typeof source] as any) || "";
      }
    });
    return data;
  }, [
    formFields,
    environmentDetails,
    hasFetchedEnvironment,
    historyEnvironmentDetails,
    documentationId,
    existingData,
  ]);

  const [formData, setFormData] =
    useState<Record<string, any>>(initializeFormData);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);

  // Reset form when formFields or environment change
  useEffect(() => {
    setFormData(initializeFormData());
  }, [initializeFormData]);

  // Reset update success status when modal opens
  useEffect(() => {
    dispatch(projectsSliceAction.resetIntegrationUpdateStatus());
  }, [dispatch]);

  const handleInputChange = (fieldName: string, value: any) => {
    if (formLocked) return;
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const getFileDisplayName = (
    file: File | string | null | undefined,
  ): string => {
    if (file instanceof File) {
      return file.name;
    }
    return file || "";
  };

  const handleFileViewClick = (file: File | string | null | undefined) => {
    // Only dispatch saga action if file is a string (S3 file key), not a File object
    if (typeof file === "string" && file) {
      dispatch(getFileViewUrlSagaAction(file, "view"));
    }
  };

  // Check if all required fields are filled
  const isFormValid = useMemo(() => {
    return formFields.every((field) => {
      if (!field.required) return true;
      const value = formData[field.name];
      if (field.type === "file") {
        // File is valid if there's a new file to upload or existing file string in formData
        const formDataFile = formData[field.name];
        return (
          fileToUpload !== null ||
          (formDataFile &&
            typeof formDataFile === "string" &&
            formDataFile !== "")
        );
      }
      if (field.type === "date") {
        return value !== null;
      }
      return value && String(value).trim() !== "";
    });
  }, [formData, formFields, fileToUpload, existingData]);

  const buildPayload = (
    formStatus?: string,
  ): UpdateEnvironmentDetailsRequest => {
    const payload: UpdateEnvironmentDetailsRequest = {};
    const payloadAny: any = payload;
    formFields.forEach((field) => {
      if (field.type !== "file") {
        const value = formData[field.name];
        if (field.type === "date" && value instanceof Date) {
          payloadAny[field.name] = value.toISOString().split("T")[0];
        } else if (value !== undefined && value !== "") {
          payloadAny[field.name] = value;
        }
      }
    });
    // Add form_status if provided
    if (formStatus) {
      payload.form_status = formStatus;
    }
    // Attach documentation_id so backend can version environment by documentation
    if (documentationId !== undefined) {
      payload.documentation_id = documentationId;
    }
    return payload;
  };

  const currentFormStatus =
    environmentDetails?.form_status ||
    historyEnvironmentDetails?.form_status ||
    existingData?.form_status ||
    "Saved";

  // Check if form has any data
  const hasFormData = useMemo(() => {
    return formFields.some((field) => {
      const value = formData[field.name];
      if (field.type === "file") {
        // Check if there's a file to upload or existing file string
        return (
          fileToUpload !== null ||
          (value && typeof value === "string" && value !== "")
        );
      }
      if (field.type === "date") {
        return value !== null;
      }
      return value && String(value).trim() !== "";
    });
  }, [formData, formFields, fileToUpload]);

  // Check if form has been saved (has form_status from backend)
  const hasBeenSaved = useMemo(() => {
    return (
      environmentDetails?.form_status != null ||
      historyEnvironmentDetails?.form_status != null ||
      existingData?.form_status != null
    );
  }, [environmentDetails, historyEnvironmentDetails, existingData]);

  const statusChipClass = useMemo(() => {
    switch (currentFormStatus) {
      case "Submitted":
        return "status-chip submitted";
      case "Verified":
        return "status-chip verified";
      default:
        return "status-chip saved";
    }
  }, [currentFormStatus]);

  const handleSave = () => {
    const payload = buildPayload("Saved");
    dispatch(
      updateEnvironmentDetailsSagaAction(
        technology,
        integration,
        integrationId,
        payload,
        fileToUpload,
      ),
    );
    setFormSubmitted(true);
  };

  const handleSubmit = () => {
    const payload = buildPayload("Submitted");
    dispatch(
      updateEnvironmentDetailsSagaAction(
        technology,
        integration,
        integrationId,
        payload,
        fileToUpload,
      ),
    );
    setFormSubmitted(true);
  };

  const handleVerify = () => {
    const payload = buildPayload("Verified");
    dispatch(
      updateEnvironmentDetailsSagaAction(
        technology,
        integration,
        integrationId,
        payload,
        fileToUpload,
      ),
    );
    setFormSubmitted(true);
  };

  const handleModalClose = () => {
    if (!status.integrationUpdateLoading) {
      onClose();
      // Only refetch project details if skipProjectDetailsRefetch is false
      // This prevents resetting form data in ValidationHistoryDetail view
      if (
        !skipProjectDetailsRefetch &&
        formSubmitted &&
        status.integrationUpdateSuccess &&
        projectDetails?.project_name &&
        partnerDetails?.ID
      ) {
        dispatch(
          getProjectDetailsSagaAction(
            projectDetails.project_name,
            partnerDetails.ID
          )
        );
      }
    }
  };

  const renderField = (field: FormFieldConfig) => {
    const value = formData[field.name];

    switch (field.type) {
      case "text":
        return (
          <div className="form-row" key={field.name}>
            <div className="form-field">
              <TextField
                label={field.label}
                id={field.name}
                name={field.name}
                placeholder={field.placeholder}
                value={value || ""}
                theme="dark"
                onChange={(e) =>
                  handleInputChange(field.name, e.currentTarget.value)
                }
                isRequired={field.required}
                disabled={formLocked}
              />
            </div>
          </div>
        );

      case "textarea":
        return (
          <div className="form-row" key={field.name}>
            <div className="form-field">
              <TextArea
                label={field.label}
                id={field.name}
                name={field.name}
                placeholder={field.placeholder}
                value={value || ""}
                theme="dark"
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  handleInputChange(field.name, e.currentTarget.value)
                }
                isRequired={field.required}
                maxLength={500}
                isDisplayCharCount={false}
                disabled={formLocked}
              />
              {field.helpText && (
                <div className="field-help-text">{field.helpText}</div>
              )}
            </div>
          </div>
        );

      case "date":
        return (
          <div className="form-row" key={field.name}>
            <div className="form-field">
              <DatePicker
                onChange={(date) => handleInputChange(field.name, date)}
                id={field.name}
                name={field.name}
                label={field.label}
                format="dd/MM/yyyy"
                placeholder={field.placeholder || "dd/mm/yyyy"}
                theme="dark"
                value={value}
                isRequired={field.required}
                disabled={formLocked}
                isDisabled={formLocked}
              />
            </div>
          </div>
        );

      case "file":
        const formDataFile = formData[field.name];

        const existingFileString: string | null =
          formDataFile &&
          typeof formDataFile === "string" &&
          formDataFile !== ""
            ? formDataFile
            : null;

        return (
          <div className="form-row" key={field.name}>
            <div className="form-field-file-upload">
              <div className="left">
                <label className="file-upload-label">
                  {field.label} {field.required && <span>*</span>}
                </label>
                <div className="file-upload-hint">
                  Maximum upload file size: 10MB.
                </div>
              </div>
              <div className="right">
                <div className="file-upload-container">
                  {!formLocked && (
                  <FileUploader
                    buttonConfig={{ label: "Browse" }}
                    isMultiple={false}
                    onUpload={(e, data) => {
                      if (formLocked) return;
                      if (data && data.length > 0) {
                        const file = data[0].fileDetails;
                        if (file instanceof File) {
                          setFileToUpload(file);
                          setFormData((prev) => ({
                            ...prev,
                            [field.name]: "",
                          }));
                        }
                      }
                    }}
                    validateConfig={[
                      {
                        maxFileSize: {
                          maxSizeInMb: 10,
                          message: "File exceeds 10MB size limit.",
                        },
                      },
                    ]}
                  />
                  )}
                </div>
                {existingFileString && (
                  <div className="file-display-box">
                    <span
                      className="file-display-name"
                      onClick={() => handleFileViewClick(existingFileString)}
                    >
                      {getFileDisplayName(existingFileString)}
                    </span>
                    {!formLocked && (
                    <span
                      className="file-display-close"
                      onClick={() => {
                        setFileToUpload(null);
                        setFormData((prev) => ({
                          ...prev,
                          [field.name]: "",
                        }));
                      }}
                    >
                      <Iconography icon="xmark" size={IconographySizes.Small} />
                    </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // TEMPORARY: use showPartnerButtons so data-entry user sees "Complete" title; revert to: getFormTitle(technology, integration, isHIDUser)
  const modalTitle = getFormTitle(technology, integration, !showPartnerButtons);

  const isSubmitting = status.integrationUpdateLoading;
  const isSuccess = status.integrationUpdateSuccess;

  return (
    <Modal
      headerText={modalTitle}
      width={800}
      onClose={handleModalClose}
      className="environment-details-modal"
    >
      {formSubmitted ? (
        <div className="status-message-modal-container">
          {isSubmitting ? (
            <Loader variant={LoaderSizes.large} />
          ) : isSuccess ? (
            <>
              <Iconography
                icon="circleCheck"
                size={IconographySizes.XLarge}
                theme={IconographyThemes.Success}
              />
              <div className="message">
                Environment details saved successfully
              </div>
              <Button
                variant="primary"
                label="Continue"
                onClick={handleModalClose}
              />
            </>
          ) : (
            <>
              <Iconography
                icon="circleXmark"
                size={IconographySizes.XLarge}
                theme={IconographyThemes.Error}
              />
              <div className="message">
                Failed to save environment details. Please try again.
              </div>
              <Button
                variant="primary"
                label="Continue"
                onClick={handleModalClose}
              />
            </>
          )}
        </div>
      ) : environmentStatus.loading ? (
        <div className="status-message-modal-container">
          <Loader variant={LoaderSizes.large} />
        </div>
      ) : (
        <div className="modal-content">
          <div className="form-subtitle">
            <strong>Technology:</strong> {technology} |{" "}
            <strong>Integration:</strong> {integration}
          </div>

          {hasFormData && hasBeenSaved && (
            <div className="form-status">
              <span className={statusChipClass}>{currentFormStatus}</span>
            </div>
          )}

          <div className="environment-form-container">
            {formFields.map((field) => renderField(field))}
          </div>

          <div className="modal-footer split">
            <div className="footer-left">
              <Button
                variant="flat"
                label="Cancel"
                normalCase={true}
                onClick={handleModalClose}
              />
            </div>
            <div className="footer-right">
              {/* TEMPORARY: use showPartnerButtons so data-entry user sees Save/Submit. Revert to: isHIDUser ? (Verify...) : (Save/Submit...) */}
              {showPartnerButtons ? (
                <>
                  <Button
                    variant="secondary"
                    label="Save"
                    normalCase={true}
                    onClick={handleSave}
                    disabled={
                      disableSaveSubmit ||
                      partnerActionsLocked ||
                      currentFormStatus === "Verified" ||
                      validationStatus === "Submitted"
                    }
                  />
                  <Button
                    variant="primary"
                    label="Submit"
                    normalCase={true}
                    onClick={handleSubmit}
                    disabled={
                      disableSaveSubmit ||
                      partnerActionsLocked ||
                      !isFormValid ||
                      currentFormStatus === "Verified" ||
                      validationStatus === "Submitted"
                    }
                  />
                </>
              ) : (
                <Button
                  variant="primary"
                  label="Verify"
                  normalCase={true}
                  onClick={handleVerify}
                  disabled={
                    currentFormStatus !== "Submitted" || approvalActionsLocked
                  }
                />
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default EnvironmentDetailsModal;
